import { observer } from "mobx-react";
import * as React from "react";
import styled from "styled-components";

import Button from "@client/components/ui/Button";
import StatusImage from "@client/components/ui/StatusImage";
import Style from "@client/utils/Style";
import { ItemComponentProps, ListItem } from "@typings/List";

const Wrapper = styled.div`
  margin-top: 8px;
  overflow: auto;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  min-width: 924px;
`;
const Separator = styled.div`
  height: 1px;
  background: ${Style.palette.grayscale[1]};
`;
const Loading = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  flex-grow: 1;
`;
const LoadingText = styled.div`
  font-size: 18px;
  margin-top: 39px;
`;
const List = styled.div`
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  overflow: hidden;
`;

const NoResults = styled.div`
  margin-top: 39px;
  text-align: center;
`;
const EmptyList = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;
  justify-content: center;
  flex-grow: 1;
`;
const EmptyListImage = styled.div<{ source: string }>`
  width: 96px;
  height: 96px;
  background: url("${(props: any) => props.source}") center no-repeat;
  margin-bottom: 46px;
  background-size: contain;
`;
const EmptyListMessage = styled.div`
  font-size: 18px;
`;
const EmptyListExtraMessage = styled.div`
  text-align: center;
  margin: 10px 0 25px 0;
`;
type Props = {
  items: any[];
  selectedItems: any[];
  loading: boolean;
  selectionEnabled: boolean;
  onSelectedChange: (item: ListItem, checked: boolean) => void;
  onItemClick: (item: ListItem) => void;
  renderItemComponent: (componentProps: ItemComponentProps) => React.ReactNode;
  showEmptyList: boolean;
  emptyListImage: string | null;
  emptyListMessage?: string;
  emptyListExtraMessage?: string;
  emptyListButtonLabel?: string;
  onEmptyListButtonClick?: () => void;
};
@observer
class FilterListList extends React.Component<Props> {
  renderList() {
    if (!this.props.items || this.props.items.length === 0) {
      return null;
    }

    return (
      <List>
        {this.props.items.map(item => {
          const selected = Boolean(
            this.props.selectedItems.find(i => i.id === item.id),
          );
          return this.props.renderItemComponent({
            key: item.id,
            item,
            selected,
            selectionEnabled: this.props.selectionEnabled,
            onClick: () => {
              this.props.onItemClick(item);
            },
            onSelectedChange: checked => {
              this.props.onSelectedChange(item, checked);
            },
          });
        })}
      </List>
    );
  }

  renderLoading() {
    return (
      <Loading>
        <StatusImage loading />
        <LoadingText>Loading ...</LoadingText>
      </Loading>
    );
  }

  renderNoResults() {
    return <NoResults>No results</NoResults>;
  }

  renderEmptyList() {
    const renderImage = () => {
      if (this.props.emptyListImage) {
        return <EmptyListImage source={this.props.emptyListImage} />;
      }
      return null;
    };

    const renderButton = () => {
      if (this.props.emptyListButtonLabel) {
        return (
          <Button onClick={this.props.onEmptyListButtonClick}>
            {this.props.emptyListButtonLabel}
          </Button>
        );
      }
      return null;
    };

    return (
      <EmptyList>
        {renderImage()}
        <EmptyListMessage>{this.props.emptyListMessage}</EmptyListMessage>
        <EmptyListExtraMessage>
          {this.props.emptyListExtraMessage}
        </EmptyListExtraMessage>
        {renderButton()}
      </EmptyList>
    );
  }

  render() {
    const renderContent = () => {
      if (this.props.loading) {
        return this.renderLoading();
      }

      if (this.props.showEmptyList) {
        return this.renderEmptyList();
      }

      if (this.props.items.length === 0) {
        return this.renderNoResults();
      }

      return this.renderList();
    };

    return (
      <Wrapper>
        {this.props.loading ||
        this.props.items.length === 0 ||
        this.props.showEmptyList ? (
          <Separator />
        ) : null}
        {renderContent()}
      </Wrapper>
    );
  }
}

export default FilterListList;
